/**
 * Print position step + live garment preview (product page B, 2026-10-13).
 * The gallery shows a blank garment packshot with the design composited on top; the client picks
 * one of five positions, a size (L / M / S) and nudges ±3 cm. Everything stays inside the print
 * boxes agreed with the printer (GENERATOR.md / spaustuvės spec 2026-10-13). Output: hidden form
 * fields Position, Print size, Offset (cm) → line item properties; the print file is built from them.
 *
 * Geometry = tee packshots (813/814/815) 2000×2268, the same numbers as mockup.py BLANKS (calibrated by garment length,
 * 2026-10-13): px per cm, chest centre x, collar seam y (front), back neck y, visible body width (cylinder wrap).
 * The packshot is one size (813 M, 814 S, 815 age 10); the preview shows the garment size the catalogue mockups use
 * (men S, women M, kids 134 ≈ '8') by scaling px/cm with the half-chest ratio, exactly like mockup.py compose(size=…).
 * Boxes = print standard v3 (DESIGN.md §7, 2026-10-13), per garment size in SIZE_CLASS below: DIDELIS 39 × 44 (813 XL–3XL,
 * 430 L–2XL), VIDUTINIS 33 × 37.3 (813 S–L, 814 M–2XL, 430 XS–M), MAŽAS 23.5 × 26.5 (814 XS–S, 815 134–158), MINI 20 × 22.5
 * (815 110–122), hoodie 33 × 20.5–28; EMBLEMA 10 × 10 (kids 8 × 8) with its top 9.5 cm below the collar; design top
 * 7 / 6 / 5 cm below the seam.
 * 2026-10-16: the old 26 × 32.5 / 24 × 30 / 20 × 25 boxes and width-based px/cm drew the sketch ~35 % smaller than the
 * catalogue mockup of the same design (ARVI, The Emperor). Checked by scripts/test-position-geometry.py.
 */

const GEO = {
  Men: { code: '813', pxcm: ((2041 - 214) / 72) * (51 / 47), cx: 995, collar: 378, back: 215, visW: 939, box: [33, 37.3], top: 7, backTop: 8, small: 10, smallTop: 9.5, off: 10 },
  Women: { code: '814', pxcm: ((2098 - 183) / 64) * (46 / 50), cx: 1008, collar: 418, back: 225, visW: 885, box: [33, 37.3], top: 6, backTop: 8, small: 10, smallTop: 9.5, off: 10 },
  Kids: { code: '815', pxcm: ((2098 - 187) / 58) * (43 / 40), cx: 1010, collar: 390, back: 215, visW: 915, box: [23.5, 26.5], top: 5, backTop: 6, small: 8, smallTop: 9.5, off: 7 },
  // 430 sweatshirt (internal key Crew) / 431 hoodie (ARVI 2026-10-17: sold from day one). Packshots are 1400 × 1588 (M, half chest 60),
  // same ratio as SHOT, so mockup.py pixels × 2000/1400. Crew shown in XS like the catalogue (box 33 × 37.3); the hoodie
  // front box is 33 × 24 above the kangaroo pocket (M, assumption until the printer confirms), the back has no pocket.
  Crew: { code: '430', pxcm: ((1424 - 163) / 72) * (2000 / 1400) * (60 / 52), cx: 696 * (2000 / 1400), collar: 235 * (2000 / 1400), back: 190 * (2000 / 1400), visW: 645 * (2000 / 1400), box: [33, 37.3], top: 7, backTop: 8, small: 10, smallTop: 9.5, off: 10 },
  // 2026-10-18 (ARVI): on the back the L print sat on the hood – the hood tip lies ~11 cm below the back neck on the
  // packshot, so the back print starts 13 cm down and is one step smaller (30 × 34) until the printer confirms
  Hoodie: { code: '431', pxcm: ((1424 - 163) / 72) * (2000 / 1400), cx: 696 * (2000 / 1400), collar: 330 * (2000 / 1400), back: 300 * (2000 / 1400), visW: 621 * (2000 / 1400), box: [33, 24], backBox: [30, 34], top: 6, backTop: 13, small: 10, smallTop: 9.5, off: 10 },
};
/**
 * Half chest (cm) per garment size – the size guide. `pxcm` above is for the catalogue size (men S, women M, kids 134,
 * crew XS, hoodie M); once the shopper picks a size the garment is drawn in that size: the same print looks smaller on
 * a 3XL than on an S (ARVI 2026-10-18: „spaudžiant kitą dydį, niekas nevyksta“).
 */
const HALF_CHEST = {
  Men: { ref: 47, S: 47, M: 51, L: 55, XL: 59, '2XL': 64, '3XL': 70 },
  Women: { ref: 50, XS: 42, S: 46, M: 50, L: 54, XL: 58, '2XL': 64 },
  Kids: { ref: 40, 4: 34, 6: 37, 8: 40, 10: 43, 12: 46 },
  Crew: { ref: 52, XS: 52, S: 56, M: 60, L: 64, XL: 68, '2XL': 72 },
  Hoodie: { ref: 60, XS: 52, S: 56, M: 60, L: 64, XL: 68, '2XL': 72 },
};
const sizeFactor = (fit, size) => {
  const t = HALF_CHEST[fit];
  const hc = t && size ? t[size] || t[parseInt(size, 10)] : 0;
  return t && hc ? t.ref / hc : 1;
};
/**
 * Front print box (cm) per garment size = hom_design.py SIZES / SIZE_MAP (DESIGN.md §7, v3 + hoodie 2026-10-14).
 * GEO.box above stays the catalogue size's box; the box of the size in the cart replaces it, so a men's 3XL gets
 * 39 × 44, a women's S 23.5 × 26.5 (ARVI 2026-10-18: „visiems dydžiams tas pats konteineris“). Checked by
 * scripts/test-position-geometry.py against hom_design.SIZE_MAP.
 */
const BOX = {
  DIDELIS: [39, 44], VIDUTINIS: [33, 37.3], MAZAS: [23.5, 26.5], MINI: [20, 22.5],
  HOODIE: [33, 20.5], 'HOODIE-M': [33, 24], 'HOODIE-L': [33, 26], 'HOODIE-XL': [33, 28],
};
const SIZE_CLASS = {
  Men: { S: 'VIDUTINIS', M: 'VIDUTINIS', L: 'VIDUTINIS', XL: 'DIDELIS', '2XL': 'DIDELIS', '3XL': 'DIDELIS' },
  Women: { XS: 'MAZAS', S: 'MAZAS', M: 'VIDUTINIS', L: 'VIDUTINIS', XL: 'VIDUTINIS', '2XL': 'VIDUTINIS' },
  Kids: { 4: 'MINI', 6: 'MINI', 8: 'MAZAS', 10: 'MAZAS', 12: 'MAZAS' },
  Crew: { XS: 'VIDUTINIS', S: 'VIDUTINIS', M: 'VIDUTINIS', L: 'DIDELIS', XL: 'DIDELIS', '2XL': 'DIDELIS' },
  Hoodie: { XS: 'HOODIE', S: 'HOODIE', M: 'HOODIE-M', L: 'HOODIE-L', XL: 'HOODIE-XL', '2XL': 'HOODIE-XL' },
};
const sizeBox = (fit, size) => {
  const t = SIZE_CLASS[fit];
  const cls = t && size ? t[size] || t[parseInt(size, 10)] : '';
  return cls ? BOX[cls] : null;
};
const SHOT = { w: 2000, h: 2268 };
const SIZE = { L: 1, M: 0.85, S: 0.7 };

/** Visible width of a flat print of `flatPx` wrapped on a body cylinder of radius `r` px (mockup.py vis_width). */
const visWidth = (flatPx, r) => {
  const s = Math.min(flatPx / 2, (r * Math.PI) / 2 - 1e-3);
  return 2 * r * Math.sin(s / r);
};
const POS = {
  'front-large': { side: 'front', label: 'Front · large' },
  'front-low': { side: 'front', label: 'Front · lower, smaller' },
  'left-chest': { side: 'front', label: 'Left chest (heart)' },
  'right-chest': { side: 'front', label: 'Right chest' },
  back: { side: 'back', label: 'Back' },
};
const NUDGE_MAX = 3;

class HomPrintPosition extends HTMLElement {
  #ac = new AbortController();
  #root = null;
  #state = { pos: 'front-large', size: 'L', dx: 0, dy: 0 };
  #art = { w: 2, h: 3 };
  #mo = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#root = this.closest('hom-product');
    if (!this.#root) return;
    this.#root.addEventListener('click', this.#onClick, { signal });
    const art = this.#root.querySelector('[data-live-art]');
    if (art instanceof HTMLImageElement) {
      const measure = () => {
        if (art.naturalWidth) this.#art = { w: art.naturalWidth, h: art.naturalHeight };
        this.#render();
      };
      art.addEventListener('load', measure, { signal });
      measure();
    }
    // the product component owns fit / colour / thumbs; follow its aria-pressed changes
    this.#mo = new MutationObserver(() => this.#render());
    for (const sel of ['[data-fit-group]', '[data-colour-group]', '[data-size-group]', '[data-thumbs]']) {
      const el = this.#root.querySelector(sel);
      if (el) this.#mo.observe(el, { attributes: true, subtree: true, attributeFilter: ['aria-pressed'], childList: true });
    }
    this.#mo.observe(this.#root, { attributes: true, attributeFilter: ['data-size-shown'] });
    const live = this.#root.querySelector('[data-live]');
    const ro = live && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => this.#render()) : null;
    if (ro && live) ro.observe(live);
    signal.addEventListener('abort', () => {
      this.#mo?.disconnect();
      ro?.disconnect();
    });
    this.#render();
  }

  disconnectedCallback() {
    this.#ac.abort();
  }

  #customArt = false;
  #blankOverride = null; // the sketch studio shows the white-tee sketch on a white blank while the black version is prepared

  /**
   * Public: swap the design shown on the garment (used by the sketch studio).
   * `blankColour` draws the packshot in that colour regardless of the chosen colour (null = follow the choice).
   */
  setArt(src, blankColour = null) {
    const art = this.#root?.querySelector('[data-live-art]');
    if (!(art instanceof HTMLImageElement)) return;
    this.#blankOverride = blankColour ? String(blankColour).toLowerCase() : null;
    this.#customArt = Boolean(src);   // a sketch is shown – stop swapping the catalogue print by colour
    if (src) art.src = src;
    else art.removeAttribute('src');
    // the client may be on another gallery photo – a picked sketch always brings the live garment back (ARVI 2026-10-16)
    if (src) this.#showSide(this.#box(GEO[this.#fit()] || GEO.Men).side);
    this.#render();
  }

  /** gallery view the product component shows: 'front' | 'back' | 'life' (owner photo) | 'flat' (laid flat photo) */
  #view() {
    return this.#root?.querySelector('[data-thumbs] [aria-pressed="true"]')?.getAttribute('data-thumb') || 'front';
  }

  /** open the front or back picture of the gallery (the print just moved there) */
  #showSide(side) {
    const t = this.#root?.querySelector(`[data-thumbs] [data-thumb="${side}"]`);
    if (t instanceof HTMLButtonElement && t.getAttribute('aria-pressed') !== 'true') t.click();
  }

  #onClick = (e) => {
    const b = e.target instanceof Element ? e.target.closest('button') : null;
    if (!b) return;
    if (b.dataset.thumb !== undefined || b.dataset.fit || b.dataset.colour || b.dataset.size) {
      return queueMicrotask(() => this.#render());
    }
    if (!this.contains(b)) return;
    if (b.dataset.pos) {
      this.#state.pos = b.dataset.pos;
      this.#showSide(POS[b.dataset.pos]?.side || 'front');
    }
    else if (b.dataset.psize) this.#state.size = b.dataset.psize;
    else if (b.dataset.nudge) {
      const [dx, dy] = b.dataset.nudge.split(',').map(Number);
      this.#state.dx = Math.max(-NUDGE_MAX, Math.min(NUDGE_MAX, this.#state.dx + dx));
      this.#state.dy = Math.max(-NUDGE_MAX, Math.min(NUDGE_MAX, this.#state.dy + dy));
    } else if (b.dataset.nudgeReset !== undefined) {
      this.#state.dx = 0;
      this.#state.dy = 0;
    } else return;
    this.#render();
  };

  #fit() {
    const b = this.#root?.querySelector('[data-fit][aria-pressed="true"]');
    return (b && b.getAttribute('data-fit')) || 'Men';
  }

  #colour() {
    const b = this.#root?.querySelector('[data-colour][aria-pressed="true"]');
    return ((b && b.getAttribute('data-colour')) || 'White').toLowerCase();
  }

  /** Box in cm for the current position + size: [w, h, centreX offset cm, top cm from seam] */
  #box(g) {
    const st = this.#state;
    const k = SIZE[st.size] || 1;
    switch (st.pos) {
      case 'left-chest': // wearer's left = viewer's right, centre `off` cm from the middle (mockup.py pos="chest")
        return { w: g.small * k, h: g.small * k, cx: +g.off, top: g.smallTop, side: 'front', chest: true };
      case 'right-chest':
        return { w: g.small * k, h: g.small * k, cx: -g.off, top: g.smallTop, side: 'front', chest: true };
      case 'front-low':
        return { w: g.box[0] * 0.75 * k, h: g.box[1] * 0.75 * k, cx: 0, top: g.top + 5, side: 'front' };
      case 'back': {
        const bb = g.backBox || g.box;
        return { w: bb[0] * k, h: bb[1] * k, cx: 0, top: g.backTop, side: 'back' };
      }
      default:
        return { w: g.box[0] * k, h: g.box[1] * k, cx: 0, top: g.top, side: 'front' };
    }
  }

  #render() {
    if (!this.#root) return;
    const st = this.#state;
    const fit = this.#fit();
    const g0 = GEO[fit] || GEO.Men;
    const g = { ...g0, pxcm: g0.pxcm * sizeFactor(fit, this.#root.getAttribute('data-size-shown') || '') };
    // the box follows the size that goes to the cart (pressed chip), not only a size the shopper clicked
    const variantSize = this.#root.querySelector('[data-size-group] [data-size][aria-pressed="true"]')?.getAttribute('data-size') || '';
    g.box = sizeBox(fit, variantSize) || g0.box;
    const box = this.#box(g);
    const view = this.#view();

    // controls
    for (const b of this.querySelectorAll('[data-pos]')) b.setAttribute('aria-pressed', String(b.dataset.pos === st.pos));
    for (const b of this.querySelectorAll('[data-psize]')) b.setAttribute('aria-pressed', String(b.dataset.psize === st.size));
    const cm = this.querySelector('[data-pos-cm]');
    if (cm) cm.textContent = `${box.w.toFixed(box.w % 1 ? 1 : 0)} × ${box.h.toFixed(box.h % 1 ? 1 : 0)} cm`;
    const off = this.querySelector('[data-pos-offset]');
    if (off) off.textContent = st.dx || st.dy ? `moved ${st.dx > 0 ? '→' : st.dx < 0 ? '←' : ''}${Math.abs(st.dx) || ''}${st.dx && st.dy ? ' ' : ''}${st.dy > 0 ? '↓' : st.dy < 0 ? '↑' : ''}${Math.abs(st.dy) || ''} cm` : 'centred';
    const cur = this.#root.querySelector('[data-cur="position"]');
    if (cur) cur.textContent = `${POS[st.pos]?.label || st.pos} · ${st.size}`;

    // hidden fields
    const set = (sel, v) => {
      const el = this.#root?.querySelector(sel);
      if (el instanceof HTMLInputElement) el.value = v;
    };
    set('[data-pos-input]', POS[st.pos]?.label || st.pos);
    set('[data-pos-size-input]', `${st.size} · ${box.w.toFixed(1)} × ${box.h.toFixed(1)} cm`);
    set('[data-pos-offset-input]', `${st.dx},${st.dy}`);

    // live preview: blank + design
    const blank = this.#root.querySelector('[data-live-blank]');
    const art = this.#root.querySelector('[data-live-art]');
    const live = this.#root.querySelector('[data-live]');
    if (!(blank instanceof HTMLImageElement) || !(art instanceof HTMLImageElement) || !(live instanceof HTMLElement)) return;
    const colour = this.#blankOverride || this.#colour();
    // catalogue print: the white-tee or the black-tee version of the design follows the chosen colour (ARVI 2026-10-17)
    if (!this.#customArt && art.dataset.artWhite) {
      const want = colour === 'black' && art.dataset.artBlack ? art.dataset.artBlack : art.dataset.artWhite;
      if (art.getAttribute('src') !== want) art.src = want;
    }
    const side = view === 'back' ? 'back' : 'front';
    const want = blank.dataset.src?.replace('{code}', g.code).replace('{colour}', colour).replace('{side}', side) || '';
    if (want && blank.getAttribute('src') !== want) blank.src = want;

    // design scaled to fit the box (contain), anchored to the box top, centred on cx
    const cxCm = box.cx + st.dx;
    const topCm = box.top + st.dy;
    const scale = Math.min(box.w / this.#art.w, box.h / this.#art.h);
    const wCm = this.#art.w * scale;
    const hCm = this.#art.h * scale;
    const seamY = box.side === 'back' ? g.back : g.collar;
    const leftPx = g.cx + cxCm * g.pxcm - (wCm * g.pxcm) / 2;
    const topPx = seamY + topCm * g.pxcm;
    // large prints wrap around the body like in the catalogue mockups (mockup.py wrap_cyl): narrower, same height
    const flatPx = wCm * g.pxcm;
    const wrap = box.chest ? 1 : visWidth(flatPx, g.visW / 2) / flatPx;
    art.style.transform = wrap < 0.999 ? `scaleX(${wrap.toFixed(4)})` : '';
    // the packshot is drawn with object-fit: cover – map shot pixels through the same scale and crop
    const cw = live.clientWidth;
    const ch = live.clientHeight;
    if (cw && ch) {
      const k = Math.max(cw / SHOT.w, ch / SHOT.h);
      const ox = (cw - SHOT.w * k) / 2;
      const oy = (ch - SHOT.h * k) / 2;
      art.style.left = `${((ox + leftPx * k) / cw) * 100}%`;
      art.style.top = `${((oy + topPx * k) / ch) * 100}%`;
      art.style.width = `${((wCm * g.pxcm * k) / cw) * 100}%`;
    } else {
      art.style.left = `${(leftPx / SHOT.w) * 100}%`;
      art.style.top = `${(topPx / SHOT.h) * 100}%`;
      art.style.width = `${((wCm * g.pxcm) / SHOT.w) * 100}%`;
    }
    art.style.height = 'auto';
    // front / back pictures are drawn live; the print shows on the side it is printed on, the owner photo stays a photo
    art.hidden = box.side !== side || !art.getAttribute('src');
    live.hidden = view === 'life' || view === 'flat' || (!art.getAttribute('src') && side === 'front');
  }
}

if (!customElements.get('hom-print-position')) {
  customElements.define('hom-print-position', HomPrintPosition);
}
